import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardMedia,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import noImage from '../assets/no-image.png';
import Button from '../components/Button';
import Modal from '../components/Modal';
import PageContainer from '../layouts/PageContainer';
import { storageGet } from '../platform/storage';
import {
  V2_PAYMENT_TYPE,
  getDeliveryQuote,
  isCodPaymentType,
  isOnlinePaymentType,
} from '../services/checkout';
import { V2ApiError } from '../services/client';
import {
  clearCart,
  formatCartCustomizationSummary,
  getAppliedCouponDiscountRupee,
  getCart,
  listCartCustomizationAddOns,
} from '../services/commerce';
import { buildPilotCodOrderPayload, createPilotOrder } from '../services/orders';
import { useV2Session } from '../state/SessionProvider';

const LOCATION_KEY = 'v2_location';

function resolveDeliveryCoords(blob) {
  if (!blob) return { lat: NaN, lng: NaN };
  const lat = Number(blob.lat ?? blob.customerAddress?.latLong?.lat);
  const lng = Number(blob.lng ?? blob.customerAddress?.latLong?.lng);
  return { lat, lng };
}

function paymentSummaryLabel(paymentType) {
  const t = paymentType ?? V2_PAYMENT_TYPE.COD;
  if (isCodPaymentType(t)) return 'Cash on delivery (COD)';
  if (isOnlinePaymentType(t)) return 'Online Payment';
  return String(t);
}

function SummaryCartLine({ line }) {
  const qty = Number(line.quantity) || 0;
  const unit = Number(line.price) || 0;
  const rowPrice = unit * qty;
  const addOns = listCartCustomizationAddOns(line.customizations);
  const compactCustom = formatCartCustomizationSummary(line.customizations);
  const variantBits = [line.variant?.weight ? String(line.variant.weight) : null].filter(Boolean);

  const customizationInline =
    addOns.length > 0
      ? addOns.map((a) => (a.additionalPrice > 0 ? `${a.name} (+₹${a.additionalPrice})` : a.name))
      : compactCustom
        ? compactCustom.split(' · ')
        : [];

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, p: 1.5 }}>
        <CardMedia
          component="img"
          image={line.imageUrl || noImage}
          alt={line.name || 'Product'}
          sx={{
            width: 88,
            height: 88,
            flexShrink: 0,
            borderRadius: 1,
            objectFit: 'cover',
            bgcolor: 'grey.100',
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = noImage;
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3, minWidth: 0, pr: 0.5 }}>
              {line.name}
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} sx={{ flexShrink: 0, textAlign: 'right' }}>
              ₹{rowPrice}
            </Typography>
          </Box>
          {variantBits.length ? (
            <Typography variant="body2" color="text.secondary">
              {variantBits.join(' · ')}
            </Typography>
          ) : null}
          {customizationInline.length ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: 0.75,
                rowGap: 0.25,
                alignItems: 'baseline',
              }}
            >
              {customizationInline.map((part, i) => (
                <React.Fragment key={`${line.productId}-c-${i}`}>
                  {i > 0 ? (
                    <Box component="span" sx={{ color: 'text.disabled', userSelect: 'none' }}>
                      ·
                    </Box>
                  ) : null}
                  <Box component="span">{part}</Box>
                </React.Fragment>
              ))}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            Qty {qty}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

export default function CheckoutSummary() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state;
  const { mobile, loggedIn } = useV2Session();

  const [loc, setLoc] = React.useState(null);
  const [cart, setCart] = React.useState({
    products: [],
    totalAmount: 0,
    totalCount: 0,
    coupon: null,
    instructions: '',
  });
  const [cartLoading, setCartLoading] = React.useState(true);
  const [placing, setPlacing] = React.useState(false);
  const [placeError, setPlaceError] = React.useState('');
  const [payFailureModalOpen, setPayFailureModalOpen] = React.useState(false);
  const [delivery, setDelivery] = React.useState({
    loading: false,
    error: '',
    charge: null,
    freeApplied: false,
    freeAbove: null,
    distanceKm: null,
  });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const blob = await storageGet(LOCATION_KEY);
      if (!cancelled) setLoc(blob);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadCart = React.useCallback(async () => {
    setCartLoading(true);
    try {
      const next = await getCart();
      setCart({
        products: Array.isArray(next.products) ? next.products : [],
        totalAmount: Number(next.totalAmount) || 0,
        totalCount: Number(next.totalCount) || 0,
        coupon: next.coupon && next.coupon.couponCode ? next.coupon : null,
        instructions: typeof next.instructions === 'string' ? next.instructions : '',
      });
    } finally {
      setCartLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadCart();
  }, [loadCart]);

  React.useEffect(() => {
    const onCartChanged = () => {
      loadCart();
    };
    window.addEventListener('v2-cart-changed', onCartChanged);
    return () => window.removeEventListener('v2-cart-changed', onCartChanged);
  }, [loadCart]);

  React.useEffect(() => {
    if (state?.paymentFailed) {
      setPayFailureModalOpen(true);
    }
  }, [state?.paymentFailed]);

  const dismissPayFailureModal = React.useCallback(() => {
    setPayFailureModalOpen(false);
    const next = state && typeof state === 'object' ? { ...state } : {};
    delete next.paymentFailed;
    delete next.paymentFailureMessage;
    navigate('/v2/checkout/summary', { replace: true, state: next });
  }, [navigate, state]);

  const hasItems = cart.products.length > 0 && cart.totalCount > 0;

  React.useEffect(() => {
    if (cartLoading || !hasItems || !loc?.storeId) {
      setDelivery({
        loading: false,
        error: '',
        charge: null,
        freeApplied: false,
        freeAbove: null,
        distanceKm: null,
      });
      return undefined;
    }

    const { lat, lng } = resolveDeliveryCoords(loc);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setDelivery({
        loading: false,
        error: 'Add a delivery address with a map pin to see delivery fee.',
        charge: null,
        freeApplied: false,
        freeAbove: null,
        distanceKm: null,
      });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setDelivery((d) => ({ ...d, loading: true, error: '' }));
      try {
        const data = await getDeliveryQuote({
          storeId: loc.storeId,
          deliveryLat: lat,
          deliveryLng: lng,
          orderSubtotal: cart.totalAmount,
        });
        if (cancelled) return;
        setDelivery({
          loading: false,
          error: '',
          charge: Number(data.deliveryCharge) || 0,
          freeApplied: Boolean(data.freeDeliveryApplied),
          freeAbove: data.freeDeliveryAbove ?? null,
          distanceKm: data.distanceKm ?? null,
        });
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof V2ApiError ? e.message : 'Could not load delivery fee.';
        setDelivery({
          loading: false,
          error: msg,
          charge: null,
          freeApplied: false,
          freeAbove: null,
          distanceKm: null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cartLoading, loc, cart.totalAmount, hasItems]);

  const handlePlaceOrder = React.useCallback(async () => {
    setPlaceError('');
    if (!loggedIn || !mobile) {
      setPlaceError('Please sign in to place an order.');
      return;
    }
    if (!state?.deliveryDate || !state?.timeSlot?.id) return;
    if (!isCodPaymentType(state.paymentType)) {
      setPlaceError('Use Pay securely for online payment, or choose cash on delivery on checkout.');
      return;
    }
    const sub = Number(cart.totalAmount) || 0;
    const couponDisc = getAppliedCouponDiscountRupee(cart);
    const shipOk = delivery.charge != null && !delivery.error && !delivery.loading;
    const ship = shipOk ? Number(delivery.charge) || 0 : NaN;
    const totalAfter = sub + ship - couponDisc;
    if (!shipOk || !(totalAfter > 0)) {
      setPlaceError('Wait for delivery fee or fix cart before placing order.');
      return;
    }
    if (!loc?.storeId || !loc?.customerAddress) {
      setPlaceError('Missing store or address. Go back to checkout.');
      return;
    }

    setPlacing(true);
    try {
      const body = buildPilotCodOrderPayload({
        mobile,
        loc,
        checkoutState: state,
        cart,
        shippingCost: ship,
      });
      const res = await createPilotOrder(body);
      const orderId = res?.order?.orderId ?? res?.order?.id;
      const orderIdStr = orderId ? String(orderId) : '';

      const receiptProducts = (cart.products || []).map((line) => ({
        productId: line.productId,
        name: line.name,
        quantity: Number(line.quantity) || 0,
        price: Number(line.price) || 0,
        variant: line.variant ? { weight: line.variant.weight } : null,
      }));

      const receipt = {
        products: receiptProducts,
        subtotal: sub,
        shippingCost: ship,
        discount: couponDisc,
        appliedCoupon: cart.coupon?.couponCode ?? null,
        total: totalAfter,
        deliveryDate: state.deliveryDate,
        timeSlotLabel: state.timeSlot?.displayTime || String(state.timeSlot?.id ?? ''),
        paymentLabel: paymentSummaryLabel(state.paymentType),
      };

      await clearCart();
      navigate('/v2/order-status', {
        replace: true,
        state: orderIdStr ? { orderId: orderIdStr, receipt } : {},
      });
    } catch (e) {
      setPlaceError(e instanceof V2ApiError ? e.message : 'Could not place order.');
    } finally {
      setPlacing(false);
    }
  }, [loggedIn, mobile, state, cart, delivery, loc, navigate]);

  const handlePayOnline = React.useCallback(() => {
    setPlaceError('');
    if (!loggedIn || !mobile) {
      setPlaceError('Please sign in to continue.');
      return;
    }
    if (!state?.deliveryDate || !state?.timeSlot?.id) return;
    if (!isOnlinePaymentType(state.paymentType)) return;

    const sub = Number(cart.totalAmount) || 0;
    const couponDisc = getAppliedCouponDiscountRupee(cart);
    const shipOk = delivery.charge != null && !delivery.error && !delivery.loading;
    const ship = shipOk ? Number(delivery.charge) || 0 : NaN;
    const totalAfter = sub + ship - couponDisc;
    if (!shipOk || !(totalAfter > 0)) {
      setPlaceError('Wait for delivery fee or fix cart before paying.');
      return;
    }
    if (!loc?.storeId || !loc?.customerAddress) {
      setPlaceError('Missing store or address. Go back to checkout.');
      return;
    }

    const body = buildPilotCodOrderPayload({
      mobile,
      loc,
      checkoutState: state,
      cart,
      shippingCost: ship,
    });

    const receiptProducts = (cart.products || []).map((line) => ({
      productId: line.productId,
      name: line.name,
      quantity: Number(line.quantity) || 0,
      price: Number(line.price) || 0,
      variant: line.variant ? { weight: line.variant.weight } : null,
    }));

    const receipt = {
      products: receiptProducts,
      subtotal: sub,
      shippingCost: ship,
      discount: couponDisc,
      appliedCoupon: cart.coupon?.couponCode ?? null,
      total: totalAfter,
      deliveryDate: state.deliveryDate,
      timeSlotLabel: state.timeSlot?.displayTime || String(state.timeSlot?.id ?? ''),
      paymentLabel: paymentSummaryLabel(state.paymentType),
    };

    navigate('/v2/checkout/pay', {
      state: {
        pilotOrderPayload: body,
        receipt,
        checkoutSummaryState: {
          deliveryDate: state.deliveryDate,
          timeSlot: state.timeSlot,
          paymentType: state.paymentType,
          instructions: String(cart.instructions ?? state?.instructions ?? '').trim(),
        },
      },
    });
  }, [loggedIn, mobile, state, cart, delivery, loc, navigate]);

  if (!state?.deliveryDate || !state?.timeSlot) {
    return <Navigate to="/v2/checkout" replace />;
  }

  const addr = loc?.customerAddress;

  const summaryInstructions = String(cart.instructions ?? state?.instructions ?? '').trim();

  const subtotal = Number(cart.totalAmount) || 0;
  const couponDiscount = getAppliedCouponDiscountRupee(cart);
  const deliveryReady = hasItems && !delivery.loading && !delivery.error && delivery.charge != null;
  const grandTotal =
    deliveryReady ? Math.max(0, subtotal + delivery.charge - couponDiscount) : null;

  const codSelected = isCodPaymentType(state.paymentType);
  const onlineSelected = isOnlinePaymentType(state.paymentType);
  const canPlaceOrder =
    loggedIn &&
    Boolean(mobile) &&
    hasItems &&
    grandTotal != null &&
    grandTotal > 0 &&
    codSelected &&
    Boolean(loc?.storeId) &&
    Boolean(addr) &&
    !placing &&
    !cartLoading;

  const canStartOnlinePay =
    loggedIn &&
    Boolean(mobile) &&
    hasItems &&
    grandTotal != null &&
    grandTotal > 0 &&
    onlineSelected &&
    Boolean(loc?.storeId) &&
    Boolean(addr) &&
    !cartLoading;

  return (
    <PageContainer title="Order summary">
      
      {cartLoading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={32} />
        </Box>
      ) : !hasItems ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your bag is empty. Add items from the cart before placing an order.
          <Box sx={{ mt: 1 }}>
            <Button variant="outlined" size="small" onClick={() => navigate('/v2/cart')} sx={{ borderRadius: '5px' }}>
              Go to cart
            </Button>
          </Box>
        </Alert>
      ) : (
        <Box sx={{ mb: 2 }}>
          {cart.products.map((line) => (
            <SummaryCartLine key={String(line.productId)} line={line} />
          ))}
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Items subtotal
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              ₹{subtotal}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary">
                Delivery
              </Typography>
              {delivery.freeApplied && delivery.freeAbove ? (
                <Typography variant="caption" color="success.main" display="block">
                  Free above ₹{delivery.freeAbove}
                </Typography>
              ) : null}
              {!delivery.loading && !delivery.error && delivery.distanceKm != null ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  ~{delivery.distanceKm} km from store
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
              {delivery.loading ? (
                <CircularProgress size={22} />
              ) : delivery.error ? (
                <Typography variant="caption" color="error">
                  {delivery.error}
                </Typography>
              ) : delivery.freeApplied ? (
                <Typography variant="body1" fontWeight={700} color="success.main">
                  Free
                </Typography>
              ) : (
                <Typography variant="body1" fontWeight={700}>
                  ₹{delivery.charge}
                </Typography>
              )}
            </Box>
          </Box>
          {couponDiscount > 0 && cart.coupon?.couponCode ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Coupon ({cart.coupon.couponCode})
              </Typography>
              <Typography variant="body1" fontWeight={700} color="success.main">
                −₹{couponDiscount}
              </Typography>
            </Box>
          ) : null}
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={800}>
              Total
            </Typography>
            {grandTotal != null ? (
              <Typography variant="h6" fontWeight={800}>
                ₹{grandTotal}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {delivery.loading ? '…' : delivery.error ? '—' : '—'}
              </Typography>
            )}
          </Box>
          {delivery.error ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Item subtotal ₹{subtotal}. Delivery fee could not be included in the total above.
            </Typography>
          ) : null}
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Delivery
        </Typography>
        <Typography variant="body2">Date: {state.deliveryDate}</Typography>
        <Typography variant="body2">Slot: {state.timeSlot?.displayTime || state.timeSlot?.id}</Typography>
      </Box>

      {summaryInstructions ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Instructions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {summaryInstructions}
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Payment
        </Typography>
        <Typography variant="body2">{paymentSummaryLabel(state.paymentType)}</Typography>
      </Box>

      {addr ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Address
          </Typography>
          <Typography variant="body2">{addr.userName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {[addr.houseDetails, addr.streetDetails, addr.landmark, addr.pincode].filter(Boolean).join(', ')}
          </Typography>
        </Box>
      ) : (
        <Alert severity="info">Address is read from saved location in app storage.</Alert>
      )}

      {placeError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {placeError}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
        {codSelected ? (
          <Button
            fullWidth
            size="large"
            disabled={!canPlaceOrder}
            onClick={handlePlaceOrder}
            startIcon={placing ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ borderRadius: '5px' }}
          >
            {placing ? 'Placing order…' : 'Place order now'}
          </Button>
        ) : (
          <Button
            fullWidth
            size="large"
            disabled={!canStartOnlinePay}
            onClick={handlePayOnline}
            sx={{ borderRadius: '5px' }}
          >
            Pay securely
          </Button>
        )}
        {!loggedIn || !mobile ? (
          <Typography variant="caption" color="text.secondary">
            Sign in to place an order.
          </Typography>
        ) : !hasItems ? null : grandTotal == null ? (
          <Typography variant="caption" color="text.secondary">
            Confirm delivery fee loads above before continuing.
          </Typography>
        ) : null}
      </Box>

      <Modal open={payFailureModalOpen} onClose={dismissPayFailureModal} title="Payment did not go through">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {typeof state?.paymentFailureMessage === 'string' && state.paymentFailureMessage.trim()
            ? state.paymentFailureMessage
            : 'Your payment could not be completed. You can try again or choose cash on delivery in checkout.'}
        </Typography>
        <Button fullWidth variant="contained" onClick={dismissPayFailureModal} sx={{ borderRadius: '5px' }}>
          OK
        </Button>
      </Modal>

    </PageContainer>
  );
}
